import { Controller } from "@hotwired/stimulus";
import debounce from "lodash.debounce";

import axios from "../api/axios";
import toQueryString from "../utils/toQueryString";

// Course picker in the collection backoffice (#10895). Sibling of
// remote_search_material_controller: same two Choices.js selects (author filter
// + result list), but the result endpoint already returns Choices-shaped
// { value, label } entries, and the third filter is the course kind rather than
// a tools model.
export default class extends Controller {
  static targets = ["selectUser", "selectCourse"];

  static values = {
    urlUser: String,
    urlCourse: String,
    urlParams: Object,
    items: Array,
  };

  initialize() {
    this.debouncedUserSearch = debounce(this.requestSearchUser.bind(this), 200);
    this.debouncedCourseSearch = debounce(
      this.requestSearchCourse.bind(this),
      200
    );
  }

  async connect() {
    if (!this.hasSelectUserTarget || !this.hasSelectCourseTarget) return;

    const { default: Choices } = await import("choices.js");

    const choiceParams = {
      placeholder: true,
      placeholderValue: "Suche...",
      searchFloor: 2,
      searchResultLimit: 30,
      loadingText: "Laden...",
      noResultsText: "Keine Ergebnisse",
      noChoicesText: "Keine Ergebnisse",
      itemSelectText: "Klicke, um auszuwählen",
    };

    this.choicesUser = new Choices(this.selectUserTarget, choiceParams);
    this.choicesCourse = new Choices(this.selectCourseTarget, choiceParams);

    this.selectUserTarget.addEventListener("search", this.debouncedUserSearch);
    this.selectCourseTarget.addEventListener(
      "search",
      this.debouncedCourseSearch
    );

    this.selectUserTarget.addEventListener("choice", () => {
      this.choicesUser.clearChoices();
    });
    this.selectCourseTarget.addEventListener("choice", () => {
      this.choicesCourse.clearChoices();
    });
  }

  async search(key, query) {
    const response = await axios.get(this.buildQueryUrl(key, query));
    if (response.status === 200) {
      return response.data;
    }
    throw new Error(response.toString());
  }

  buildQueryUrl(key, query) {
    const urlParams = key === "course" ? { ...this.urlParamsValue } : {};
    urlParams.query = query;

    return `${
      key === "course" ? this.urlCourseValue : this.urlUserValue
    }${toQueryString(urlParams)}`;
  }

  async requestSearchUser() {
    const query = this.choicesUser.input.value;
    const results = await this.search("user", query);
    this.choicesUser.clearChoices();
    this.choicesUser.setChoices(results, "value", "label", true);
  }

  async requestSearchCourse() {
    const query = this.choicesCourse.input.value;
    const results = await this.search("course", query);
    // exclude courses that are already linked to the collection
    const filteredResults = results.filter(
      (course) => !this.itemsValue.includes(course.value)
    );
    this.choicesCourse.clearChoices();
    this.choicesCourse.setChoices(filteredResults, "value", "label", true);
  }

  disconnect() {
    if (this.choicesUser) {
      this.choicesUser.destroy();
    }
    if (this.choicesCourse) {
      this.choicesCourse.destroy();
    }
  }

  addUrlParams(event) {
    if (event.target.value === "") {
      this.urlParamsValue = {
        ...this.urlParamsValue,
        [event.params.urlKey]: undefined,
      };
    } else {
      this.urlParamsValue = {
        ...this.urlParamsValue,
        [event.params.urlKey]: event.target.value,
      };
    }
    this.requestSearchCourse();
  }
}
